import React, { useMemo, useEffect, useState } from 'react';
import { BsArrowLeftShort, BsArrowRightShort } from 'react-icons/bs';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import { Spinner } from 'components/Loading';
import { createSetupIntent as actionsCreateSetupIntent } from 'containers/Billing/actions';
import PaymentForm from '../FormFields/PaymentForm';
import './styles.scss';


const Step6 = ({ formData }) => {
  const history = useHistory();
  const { stepId } = useParams();
  const billing = useSelector(state => state.billing);
  const userInfo = useSelector(state => state.global?.userInfo);
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const { createSetupIntent } = bindActionCreators({
    createSetupIntent: actionsCreateSetupIntent,
  }, dispatch);

  // Check subscription status
  const existingSubscription = userInfo?.billing?.planningSubscription;
  const hasExistingSubscription = existingSubscription?.id;
  const isTrial = existingSubscription?.isTrial;
  const isExpired = existingSubscription?.isExpired;
  const remainingDays = existingSubscription?.remainingTrialDays;
  const planName = existingSubscription?.name || existingSubscription?.code || 'Active Plan';

  const handlePaymentSuccess = () => {
    // Redirect to welcome page after successful payment and trial creation
    window.location.href = `/app/pro/${formData.companyInfo.orgId}/welcome`;
  };

  const handlePaymentError = (_error) => {
    /* eslint-disable-next-line no-console */
    console.error('Payment error:', _error);
    // Error is already displayed in the form
  };

  useEffect(() => {
    // If user already has a subscription, skip payment setup
    if (hasExistingSubscription) {
      setIsLoading(false);
      return;
    }
    // Always create a fresh setup intent for new users
    const initializePayment = async () => {
      try {
        // Always create a new SetupIntent to avoid reusing a completed one
        await createSetupIntent();
        setIsLoading(false);
      } catch (err) {
        setError('Failed to initialize payment system. Please try again.');
        setIsLoading(false);
        /* eslint-disable-next-line no-console */
        console.error('Payment initialization error:', err);
      }
    };

    initializePayment();
  }, [hasExistingSubscription]); // Removed stepId and createSetupIntent to prevent excessive calls

  const stripePromise = useMemo(() => {
    return loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
  },[]);

  const optionsElements = {
    clientSecret: billing.clientSecret,
  };

  const handleBack = () => {
    history.push('/app/v2/account-setup/step/6');
  };

  const handleContinue = () => {
    // Redirect to projects page
    window.location.href = '/app/v2/projects';
  };

  const handleGoToBilling = () => {
    // Redirect to billing page for expired subscriptions
    window.location.href = '/app/v2/account/billing';
  };

  // If user has an active subscription (trial or paid)
  if (hasExistingSubscription && !isExpired) {
    return (
      <>
        <section className="stepflow_content">
          <article className="stepflow_form">
            <section className="stepflow_form-header">
              <h3 className="form-title">You Already Have a Subscription</h3>
              <p className="form-subtitle text-center">
                You're all set! You already have an active subscription.
              </p>
            </section>

            <div className="subscription-info">
              <div className="subscription-details">
                <h4>Current Subscription</h4>
                <div className="subscription-card">
                  <div className="plan-name">
                    {planName}
                  </div>
                  {isTrial && remainingDays && (
                    <div className="trial-info">
                      <span className="trial-badge">Trial</span>
                      <span className="remaining-days">
                        {remainingDays} days remaining
                      </span>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </article>
        </section>

        <section className="stepflow_footer">
          <button
            className="stepflow_footer-button"
            onClick={handleBack}
          >
            <BsArrowLeftShort size={20} /> Back
          </button>

          <button
            className="stepflow_footer-button"
            onClick={handleContinue}
          >
            Continue to Projects
            <BsArrowRightShort size={20} />
          </button>
        </section>
      </>
    );
  }

  // If user has an expired subscription
  if (hasExistingSubscription && isExpired) {
    return (
      <>
        <section className="stepflow_content">
          <article className="stepflow_form">
            <section className="stepflow_form-header">
              <h3 className="form-title">Subscription Expired</h3>
              <p className="form-subtitle text-center">
                Your subscription has expired. Please resubscribe to continue using Pro features.
              </p>
            </section>

            <div className="subscription-info">
              <div className="subscription-details">
                <h4>Previous Subscription</h4>
                <div className="subscription-card expired">
                  <div className="plan-name">
                    {planName}
                  </div>
                  <div className="expired-info">
                    <span className="expired-badge">Expired</span>
                  </div>
                </div>
              </div>

            </div>
          </article>
        </section>

        <section className="stepflow_footer">
          <button
            className="stepflow_footer-button"
            onClick={handleBack}
          >
            <BsArrowLeftShort size={20} /> Back
          </button>

          <button
            className="stepflow_footer-button primary"
            onClick={handleGoToBilling}
          >
            Go to Billing
            <BsArrowRightShort size={20} />
          </button>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="stepflow_content">
        <article className="stepflow_form">
          <section className="stepflow_form-header">
            <h3 className="form-title">One Last Step!</h3>
            <p className="form-subtitle text-center">
              Your 30-day trial is free, no charges today. Just add a card to get started.
              We’ll send you a reminder before your trial ends, and you can cancel at any time.
              Please enter your payment information below.
            </p>
          </section>

          <div className="payment-section">
            {billing.clientSecret && !isLoading ? (
              <Elements
                stripe={stripePromise}
                options={optionsElements}
              >
                <PaymentForm
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                />
              </Elements>
            ) : (
              <div className="payment-loading">
                <Spinner
                  loading
                  height={15}
                  width={5}
                  radius={5}
                  margin={2}
                />
                <p>Setting up secure payment...</p>
              </div>
            )}
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </article>
      </section>

      <section className="stepflow_footer">
        <button
          className="stepflow_footer-button"
          onClick={handleBack}
        >
          <BsArrowLeftShort size={20} /> Back
        </button>
      </section>
    </>
  );
};

export default Step6;
